import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useShamanContext } from './useShamanContext';
import { formatDateTimeKo, formatKRW, formatPhone } from '@/lib/format';
import { addDays } from 'date-fns';

interface BookingRow {
  id: string;
  starts_at: string;
  status: string;
  duration_minutes: number;
  price_krw: number;
  payment_method: string;
  customer_name: string;
  customer_phone: string;
  customer_birth_date: string;
  customer_gender: string;
}

export function ShamanHomePage() {
  const { data: ctx } = useShamanContext();
  const shamanId = ctx?.shaman.id;

  const todayQuery = useQuery({
    queryKey: ['today-bookings', shamanId],
    enabled: !!shamanId,
    queryFn: async (): Promise<BookingRow[]> => {
      const now = new Date();
      const end = addDays(now, 2);
      const { data, error } = await supabase
        .from('bookings')
        .select('id, starts_at, status, duration_minutes, price_krw, payment_method, customer_name, customer_phone, customer_birth_date, customer_gender')
        .eq('shaman_id', shamanId!)
        .gte('starts_at', now.toISOString())
        .lte('starts_at', end.toISOString())
        .in('status', ['pending_payment', 'confirmed'])
        .order('starts_at');
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <div className="space-y-4">
      <div className="card">
        <h1 className="text-xl font-bold">{ctx?.shaman.display_name_ko}님, 환영합니다</h1>
        <p className="text-base text-ink-muted mt-1">오늘과 내일 들어온 예약입니다.</p>
        <div className="mt-3 flex gap-2 flex-wrap">
          <Link to="/me/availability" className="btn-secondary">시간표 설정</Link>
          <Link to={`/b/${ctx?.shaman.slug}`} target="_blank" className="btn-secondary">내 페이지 미리보기</Link>
        </div>
      </div>

      {todayQuery.isLoading && <div className="text-ink-muted">불러오는 중…</div>}
      {!todayQuery.isLoading && !todayQuery.data?.length && (
        <div className="card text-ink-muted text-center">예약이 없습니다.</div>
      )}

      <ul className="space-y-3">
        {todayQuery.data?.map((b) => (
          <li key={b.id} className="card space-y-1">
            <div className="flex justify-between items-baseline">
              <div className="font-bold text-lg">{formatDateTimeKo(b.starts_at)}</div>
              <div className="text-base">{formatKRW(b.price_krw)}</div>
            </div>
            <div className="text-base">
              {b.customer_name} ({b.customer_gender === 'male' ? '남' : '여'} · {b.customer_birth_date}) · {formatPhone(b.customer_phone)}
            </div>
            <div className="text-base text-ink-muted">
              {b.duration_minutes}분 · {b.payment_method === 'toss_pay' ? '토스/카드' : '가상계좌'} ·{' '}
              <span className={b.status === 'confirmed' ? 'text-success font-semibold' : 'text-warning font-semibold'}>
                {b.status === 'confirmed' ? '확정' : '입금/결제 대기'}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
